import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import _extends from '@babel/runtime/helpers/extends';
import _defineProperty from '@babel/runtime/helpers/defineProperty';
import * as React from 'react';
import mergeRef from '../../../util/mergeRef.js';
import { useEventListener } from '../../../util/useEventListener.js';
import { useInteraction } from '../../../util/useInteraction.js';
import useScreenSize from '../../../util/useScreenSize.js';
import { isTouchDevice } from '@drone-js/media-query';
import { BottomSheet as BottomSheetMemoized } from '../../BottomSheet/BottomSheet.js';
import { Box } from '../../Box/Box.js';
import { candidatesSelector, isFocusableElement } from '../../FocusTrap/FocusTrap.js';
import { List } from '../../List/List.js';
import '../../Popover/index.js';
import { SearchField as SearchFieldMemoized } from '../../SearchField/SearchField.js';
import { Popover } from '../../Popover/Popover.js';

var _excluded = ["popover"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
var FocusType = /*#__PURE__*/function (FocusType) {
  FocusType[FocusType["Initial"] = 0] = "Initial";
  FocusType[FocusType["Forward"] = 1] = "Forward";
  FocusType[FocusType["Backward"] = 2] = "Backward";
  return FocusType;
}(FocusType || {});
var getSearchContent = function getSearchContent(searchProps, paddings, onSearchChange, ref) {
  var _searchProps$searchCl, _searchProps$searchPl;
  return /*#__PURE__*/React.createElement(Box, paddings, /*#__PURE__*/React.createElement(SearchFieldMemoized, {
    onClear: function onClear() {},
    leadingAsset: {
      id: 'fill.symbol.search'
    },
    clearLabel: (_searchProps$searchCl = searchProps === null || searchProps === void 0 ? void 0 : searchProps.searchClearLabelText) !== null && _searchProps$searchCl !== void 0 ? _searchProps$searchCl : 'Clear search',
    variant: "input",
    placeholderText: (_searchProps$searchPl = searchProps === null || searchProps === void 0 ? void 0 : searchProps.searchPlaceholderText) !== null && _searchProps$searchPl !== void 0 ? _searchProps$searchPl : 'Search',
    inputText: searchProps === null || searchProps === void 0 ? void 0 : searchProps.searchText,
    onChange: function onChange(elem) {
      onSearchChange === null || onSearchChange === void 0 || onSearchChange(elem.currentTarget.value);
      searchProps === null || searchProps === void 0 || searchProps.setSearchString(elem.currentTarget.value);
    },
    ref: ref
  }));
};
var DropdownListContent = function DropdownListContent(_ref, variant) {
  var items = _ref.items,
    visible = _ref.visible,
    search = _ref.search,
    searchProps = _ref.searchProps,
    onSearchChange = _ref.onSearchChange,
    _ref$enableArrowKeyFo = _ref.enableArrowKeyFocusNavigationOnPopover,
    enableArrowKeyFocusNavigationOnPopover = _ref$enableArrowKeyFo === void 0 ? true : _ref$enableArrowKeyFo,
    maxHeight = _ref.maxHeight;
  var listBoxRef = React.useRef(null);
  var searchFieldRef = React.useRef(null);
  var focusableElements = React.useRef(null);

  /**
   * Move focus to the next focusable element.
   *  If no element is selected, move focus to the first focusable element.
   * @param elements List of focusable elements
   * @param focusType Type of focus movement
   *
   */
  var moveFocus = React.useCallback(function (elements, focusType) {
    if (focusType === FocusType.Initial) {
      var checkedElement = elements.findIndex(function (element) {
        return element.checked;
      });
      if (searchFieldRef.current && checkedElement === -1) {
        searchFieldRef.current.focus();
      } else {
        var nextElementIndex = checkedElement === -1 ? 0 : checkedElement;
        var nextElement = elements[nextElementIndex];
        nextElement.focus();
      }
    } else if (focusType === FocusType.Forward || focusType === FocusType.Backward) {
      var count = elements.length;
      var prevIndex = elements.indexOf(document.activeElement);
      var _nextElementIndex = (focusType === FocusType.Forward ? prevIndex + 1 : prevIndex + count - 1) % count;
      var _nextElement = elements[_nextElementIndex];
      _nextElement.focus();
    }
  }, [searchFieldRef]);

  /**
   * Allow keyboard navigation on focusable elements in Dropdown List using Arrow Keys
   * This supports only for Popover variant
   */
  var handleKeyDownMoveFocusWithArrowKey = React.useCallback(function (event) {
    var elements = focusableElements.current;
    if (!elements || elements.length === 0) {
      return;
    }
    // Handle RTL/ remove right left (discuss at point) when creating hook
    if (event.key === 'ArrowDown' || event.key === 'ArrowRight') {
      moveFocus(elements, FocusType.Forward);
      event.preventDefault();
    } else if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') {
      moveFocus(elements, FocusType.Backward);
      event.preventDefault();
    }
  }, [moveFocus]);
  var _useInteraction = useInteraction({
      onKeyDown: handleKeyDownMoveFocusWithArrowKey
    }),
    interactionProps = _useInteraction.interactionProps;
  var getFocusableElements = function getFocusableElements() {
    if (listBoxRef.current) {
      var allElements = listBoxRef.current.querySelectorAll(candidatesSelector);
      focusableElements.current = Array.from(allElements).filter(isFocusableElement);
    }
  };
  React.useEffect(function () {
    // Must run to get list on change of listBoxRef, visible and items
    getFocusableElements();
  }, [listBoxRef, visible, items]);
  React.useEffect(function () {
    var elements = focusableElements.current;
    if (elements && elements.length > 0 && visible) {
      moveFocus(elements, FocusType.Initial);
    }
  }, [listBoxRef, moveFocus, visible]);
  return /*#__PURE__*/React.createElement(React.Fragment, null, search && variant === 'popover' && getSearchContent(searchProps, {
    pb: '8'
  }, onSearchChange, searchFieldRef), /*#__PURE__*/React.createElement(Box, {
    display: variant === 'popover' ? 'flex' : 'block',
    maxHeight: variant === 'popover' ? maxHeight : undefined,
    overflowY: "auto"
  }, /*#__PURE__*/React.createElement(List, _extends({
    role: "listbox",
    ref: listBoxRef
  }, enableArrowKeyFocusNavigationOnPopover && _objectSpread({}, interactionProps)), items)));
};
var DropdownListBottomSheet = function DropdownListBottomSheet(props) {
  var title = props.title,
    zIndex = props.zIndex,
    visible = props.visible,
    state = props.state,
    onVisibilityChange = props.onVisibilityChange,
    onSearchChange = props.onSearchChange,
    closeButton = props.closeButton,
    portalWrapperComponent = props.portalWrapperComponent,
    children = props.children,
    search = props.search,
    searchProps = props.searchProps;
  var disabled = state === 'disabled';
  var triggerElement = React.useMemo(function () {
    var onChange = function onChange() {
      return onVisibilityChange(true);
    };
    return /*#__PURE__*/React.createElement(Box, {
      onClick: disabled ? undefined : onChange
    }, children);
  }, [children, disabled, onVisibilityChange]);
  var Wrapper = portalWrapperComponent !== null && portalWrapperComponent !== void 0 ? portalWrapperComponent : React.Fragment;
  return /*#__PURE__*/React.createElement(React.Fragment, null, triggerElement, /*#__PURE__*/React.createElement(Wrapper, null, /*#__PURE__*/React.createElement(BottomSheetMemoized, _extends({
    title: title,
    visible: visible,
    closeButton: closeButton,
    zIndex: zIndex,
    onChange: onVisibilityChange,
    focusableContainer: false,
    height: search ? 'full-screen' : undefined
  }, search && {
    contentHeading: getSearchContent(searchProps, {
      p: '16'
    }, onSearchChange)
  }), DropdownListContent(props, 'bottomsheet'))));
};
var DropdownListPopover = function DropdownListPopover(props) {
  var _props$popover$pointe, _props$popover, _props$popover2, _props$popover3;
  var zIndex = props.zIndex,
    visible = props.visible,
    state = props.state,
    onVisibilityChange = props.onVisibilityChange,
    triggerFlex = props.triggerFlex,
    children = props.children;
  var triggerElementRef = React.useRef(null);
  var handleEscKey = React.useCallback(function (event) {
    if (event instanceof KeyboardEvent && event.key === 'Escape') {
      var _triggerElementRef$cu;
      onVisibilityChange(false);
      (_triggerElementRef$cu = triggerElementRef.current) === null || _triggerElementRef$cu === void 0 || _triggerElementRef$cu.focus();
    }
  }, [onVisibilityChange]);
  useEventListener({
    eventName: 'keydown',
    handler: handleEscKey,
    attached: visible,
    capture: false
  });
  var triggerElement = React.useMemo(function () {
    return React.Children.only(children) && React.Children.map(children, function (child) {
      if (/*#__PURE__*/React.isValidElement(child)) {
        var childElement = child;
        var attributes = _objectSpread(_objectSpread({}, child.props), {}, {
          // @ts-expect-error ref can be found in child, but it is not defined in ReactElement
          ref: mergeRef(childElement.ref, triggerElementRef),
          'aria-haspopup': 'listbox',
          'aria-expanded': visible
        });
        return /*#__PURE__*/React.cloneElement(childElement, _objectSpread({}, attributes));
      }
      return child;
    });
  }, [children, visible]);
  return /*#__PURE__*/React.createElement(Box, {
    display: "flex",
    flex: 1,
    pointerEvents: state === 'disabled' ? 'none' : undefined
  }, /*#__PURE__*/React.createElement(Popover, {
    content: DropdownListContent(props, 'popover'),
    pointerPosition: (_props$popover$pointe = (_props$popover = props.popover) === null || _props$popover === void 0 ? void 0 : _props$popover.pointerPosition) !== null && _props$popover$pointe !== void 0 ? _props$popover$pointe : 'start-above',
    zIndex: zIndex,
    visible: visible,
    onVisibilityChange: onVisibilityChange,
    flex: triggerFlex,
    fixed: (_props$popover2 = props.popover) === null || _props$popover2 === void 0 ? void 0 : _props$popover2.fixed,
    usePortal: (_props$popover3 = props.popover) === null || _props$popover3 === void 0 ? void 0 : _props$popover3.usePortal,
    p: props.padding,
    flexWrapper: {
      flex: 1
    },
    isContentControlledAndUnannounced: true // Trigger element doesn't need SR to read out content inside Popover
  }, triggerElement));
};
var shouldUseBottomSheet = function shouldUseBottomSheet(props, screenSize) {
  switch (props.variant) {
    case 'popover':
      return false;
    case 'bottomsheet':
      return true;
    default:
      return isTouchDevice() && screenSize === 's';
  }
};

/**
 * DropdownList is still in beta stage, some of the functionality is not yet completed.
 */
var DropdownListV2 = function DropdownListV2(_ref2) {
  var popover = _ref2.popover,
    props = _objectWithoutProperties(_ref2, _excluded);
  var screenSize = useScreenSize();
  var _React$useState = React.useState(''),
    _React$useState2 = _slicedToArray(_React$useState, 2),
    searchText = _React$useState2[0],
    setSearchText = _React$useState2[1];
  var searchProps = {
    searchText: searchText,
    setSearchString: setSearchText,
    searchPlaceholderText: props.searchPlaceholderText,
    searchClearLabelText: props.searchClearLabelText
  };
  return shouldUseBottomSheet(props, screenSize) ? /*#__PURE__*/React.createElement(DropdownListBottomSheet, _extends({}, props, {
    searchProps: searchProps
  })) : /*#__PURE__*/React.createElement(DropdownListPopover, _extends({}, props, {
    popover: popover,
    searchProps: searchProps
  }));
};
DropdownListV2.defaultProps = {
  maxHeight: '45vh'
};
DropdownListV2.displayName = 'DropdownListV2';

export { DropdownListV2 };
//# sourceMappingURL=DropdownListV2.js.map
